<template>
  <div class="editor-wrap">
    <notice-publish-template
      v-if="showChooseTemplate && showChooseBtn"
      ref="noticePublishTemplate"
      class="m-b-10"
      :purchase-type="purchaseType"
      :notice-type="noticeType"
      :required="required"
      :label-width="labelWidth"
      :is-company-first="isCompanyFirst"
      @confirmed="selectTemplate"
    />
    <vue-ueditor-wrap
      v-if="!disabled"
      v-model="editor"
      :destroy="true"
      :config="editorConfig"
      :editor-id="editorId"
      @ready="editorReady"
    />
    <div v-else v-dompurify-html="editor" class="editor"></div>
  </div>
</template>

<script>
import VueUeditorWrap from 'vue-ueditor-wrap'
import NoticePublishTemplate from './NoticePublishTemplate.vue'

import { getTemplateNoticeDetailApi } from '@/api/template'

import { mapActions } from 'vuex'
import Cookies from 'js-cookie'

export default {
  name: 'UEditor',

  components: {
    VueUeditorWrap,
    NoticePublishTemplate
  },

  model: {
    prop: 'value',
    event: 'change'
  },

  props: {
    value: {
      type: String,
      default: ''
    },
    editorId: {
      type: String,
      default: 'editor-demo-01'
    },
    // UEditor是否可编辑
    disabled: {
      type: Boolean,
      default: false
    },
    // 需要对UEditor额外的配置
    config: {
      type: Object,
      default: () => {}
    },
    // UEditor是否默认使用宋体字体
    useSimsun: {
      type: Boolean,
      default: true
    },

    // 是否显示选择模板
    showChooseTemplate: {
      type: Boolean,
      default: false
    },

    // 模板类型
    purchaseType: {
      type: [String, Number],
      default: ''
    },
    // 公告类型
    noticeType: {
      type: [String, Number],
      default: ''
    },
    required: {
      type: [Boolean],
      default: true
    },
    labelWidth: {
      type: String,
      default: '120px'
    },

    // 是否企业模板在前面
    isCompanyFirst: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      editorInstance: null,

      showChooseBtn: false,

      myConfig: {
        // 编辑器不自动被内容撑高
        autoHeightEnabled: true,
        // 初始容器高度
        initialFrameHeight: 500,
        // 初始容器宽度
        initialFrameWidth: '100%',
        // 上传文件接口
        serverUrl: `${
          process.env.VUE_APP_FILE_API
        }/resources/ueditor?x-jczh-ac=${Cookies.get('X-JCZH-AC')}`,
        // UEditor 资源文件的存放路径，如果你使用的是 vue-cli 生成的项目，通常不需要设置该选项，vue-ueditor-wrap 会自动处理常见的情况，如果需要特殊配置，参考下方的常见问题2
        UEDITOR_HOME_URL: process.env.BASE_URL + 'UEditor/',
        autoFloatEnabled: true,
        topOffset: 64,
        zIndex: 100,
        enableAutoSave: false,
        toolbars: [
          [
            'undo',
            'redo',
            '|',
            'bold',
            'italic',
            'underline',
            'fontborder',
            'strikethrough',
            'superscript',
            'subscript',
            'removeformat',
            'formatmatch',
            'autotypeset',
            'blockquote',
            'pasteplain',
            '|',
            'forecolor',
            'backcolor',
            'insertorderedlist',
            'insertunorderedlist',
            'selectall',
            'cleardoc',
            '|',
            'rowspacingtop',
            'rowspacingbottom',
            'lineheight',
            '|',
            'customstyle',
            'paragraph',
            'fontsize',
            '|',
            'directionalityltr',
            'directionalityrtl',
            'indent',
            '|',
            'justifyleft',
            'justifycenter',
            'justifyright',
            'justifyjustify',
            '|',
            'touppercase',
            'tolowercase',
            '|',
            'link',
            'unlink',
            'anchor',
            '|',
            'imagenone',
            'imageleft',
            'imageright',
            'imagecenter',
            '|',
            'simpleupload',
            'insertimage',
            'emotion',
            'attachment',
            'map',
            'pagebreak',
            '|',
            '|',
            'horizontal',
            'date',
            'time',
            'spechars',
            '|',
            'inserttable',
            'deletetable',
            'insertparagraphbeforetable',
            'insertrow',
            'deleterow',
            'insertcol',
            'deletecol',
            'mergecells',
            'mergeright',
            'mergedown',
            'splittocells',
            'splittorows',
            'splittocols',
            'charts'
          ]
        ]
      }
    }
  },

  computed: {
    editor: {
      get() {
        return this.value || ''
      },
      set(val) {
        if (this.disabled && !val) {
          this.editorInstance && this.editorInstance.setContent(this.value)
        } else {
          this.$emit('change', val)
        }
      }
    },

    editorConfig() {
      if (this.disabled) {
        return Object.assign({}, this.myConfig, {
          toolbars: [],
          initialFrameHeight: 0,
          elementPathEnabled: false,
          wordCount: false,
          readonly: true,
          enableAutoSave: false
        })
      } else {
        return Object.assign({}, this.myConfig, this.config)
      }
    }
  },

  watch: {
    disabled(val) {
      if (val) {
        this.$nextTick(() => {
          document
            .querySelectorAll(`textarea[name='editorValue']`)
            .forEach(el => {
              el.style.display = 'none'
            })
        })
      }
    }
  },

  async created() {
    this.showChooseBtn = await this.showGreyFeature({ code: 'notice-template' })
  },

  beforeDestroy() {
    window.onscroll = null
  },

  methods: {
    ...mapActions('company', ['showGreyFeature']),
    /**
     * 当设置了不可修改时，禁用编辑功能
     */
    editorReady(editorInstance) {
      this.editorInstance = editorInstance
      this.editorInstance.execCommand(
        'fontfamily',
        this.useSimsun ? '宋体' : 'Helvetica Neue'
      )
      if (this.disabled) {
        editorInstance.setDisabled()
      }
    },

    /**
     * 选择模板
     */
    selectTemplate(id) {
      this.getTemplate(id)
    },

    /**
     * 获取模板
     */
    async getTemplate(id) {
      if (!id) return
      try {
        const _data = await getTemplateNoticeDetailApi(id)
        const templateContent = _data?.data?.templateContent
        this.$emit('setTemplateContent', {
          templateId: id,
          templateContent
        })
      } catch (error) {
        this.$message.error(error.message)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.editor-wrap {
  line-height: 1.6 !important;
}

.editor {
  width: 100%;
  overflow: auto;

  ::v-deep table {
    min-width: 100% !important;
    border-collapse: collapse;
    border-spacing: 0;

    td {
      border: 1px solid #000 !important;
      //overflow: auto;
    }
  }
}
</style>
